import * as React from 'react';

import BoxBody from 'components/BoxBody';
import Button from 'components/ButtonDeprecated';
import Buttons from 'components/Buttons';
import Container from 'components/Container';
import Dialog from 'components/Dialog';
import Link from 'components/Link';
import Row from 'components/Row';
import TabPanel from 'components/TabPanel';
import Tabs from 'components/Tabs';
import Text from 'components/Text';
import LinkConstants from 'constants/LinkConstants';
import OrganizationEngagementBadge from 'organizations/components/OrganizationEngagementBadge';
import EngagementRatings, { EngagementRating } from 'organizations/constants/EngagementRatings';

import './OrganizationEngagementModal.scss';

type OrganizationEngagementModalCopyProps = {
  children: string;
  title: string;
};

function OrganizationEngagementModalCopy({
  children,
  title,
}: OrganizationEngagementModalCopyProps) {
  return (
    <Text align="left" element="p" flush>
      <Text uppercase weight="bold">
        {title}
      </Text>
      : {children}
    </Text>
  );
}

type Props = {
  eventKey: string;
  onRequestHide: () => void;
  organizationType?: 'school' | 'district';
  rating?: EngagementRating;
  utmParams: string;
};

export default function OrganizationEngagementModal({
  eventKey,
  organizationType,
  rating,
  utmParams,
  ...others
}: Props) {
  const copy = {
    active: (
      <OrganizationEngagementModalCopy title="Active">
        The majority of families at these schools have access to an official communication channel
        and receive most important updates.
      </OrganizationEngagementModalCopy>
    ),
    emerging: (
      <OrganizationEngagementModalCopy title="Emerging">
        At these schools, teachers and administrators use a variety of different communication
        channels for periodic outreach.
      </OrganizationEngagementModalCopy>
    ),
    engaged: (
      <OrganizationEngagementModalCopy title="Engaged">
        These schools maintain a regular cadence of communication with families to build trust and
        strengthen relationships.
      </OrganizationEngagementModalCopy>
    ),
    growing: (
      <OrganizationEngagementModalCopy title="Growing">
        Official communication channels at these schools help streamline the exchange of information
        between school and home.
      </OrganizationEngagementModalCopy>
    ),
  } as const;

  const title =
    organizationType === 'district'
      ? 'The featured engagement rating is the rating that appears most frequently among the schools in a district.'
      : 'How the rating is calculated';

  return (
    <Dialog
      {...others}
      title={title}
      subtext={
        <React.Fragment>
          Each school’s engagement rating is based on{' '}
          <Link to={LinkConstants.NCES} target="_blank">
            publicly available school data
          </Link>{' '}
          and usage statistics from <Link to={LinkConstants.MARKETING}>Remind</Link>.
        </React.Fragment>
      }
      className="OrganizationEngagementModal">
      <BoxBody>
        <Row>
          <Tabs
            flush={false}
            tabActive={rating ? EngagementRatings.RATINGS.indexOf(rating) : 0}
            tabAlign="center">
            {EngagementRatings.RATINGS.map(item => (
              <TabPanel
                title={<OrganizationEngagementBadge rating={item} size="small" />}
                key={item}>
                {copy[item]}
              </TabPanel>
            ))}
          </Tabs>
        </Row>
        {organizationType === 'district' ? (
          <Button
            to={`${LinkConstants.ADMIN_CONTACT}?${utmParams}&utm_content=modal`}
            eventKey={`${eventKey}.ratings_modal.cta.click`}>
            Learn more about engagement in your district
          </Button>
        ) : (
          <Container size="small">
            <Buttons axis="y">
              <Button
                to={`${LinkConstants.ADMIN_CONTACT}?${utmParams}&utm_content=modal`}
                eventKey={`${eventKey}.ratings_modal.cta.click`}>
                {rating ? 'Get started' : 'Learn more'}
              </Button>
              {rating ? (
                <Button
                  to={`${EngagementRatings.BADGES[rating].png}?${utmParams}`}
                  color="mascot"
                  download
                  level="text"
                  target="_blank"
                  weight="semibold"
                  eventKey={`${eventKey}.download_badge.click`}>
                  Download to share
                </Button>
              ) : (
                <Button
                  to={LinkConstants.ENGAGEMENT_RATINGS_HC}
                  color="mascot"
                  level="text"
                  target="_blank"
                  weight="semibold">
                  Why doesn’t this school have a rating?
                </Button>
              )}
            </Buttons>
          </Container>
        )}
      </BoxBody>
    </Dialog>
  );
}

OrganizationEngagementModal.defaultProps = {
  organizationType: 'school',
};
